import { dungeonService } from './DungeonService';
import { MonsterEntity } from '../entities/MonsterEntity';
import {
  MONSTER_CONFIGS,
  scaleMonsterStat,
  DEFAULT_STAT_RATES,
} from '../data/monsters.config';
import { rollLootTable, getTalentLootMultiplier } from './LootService';
import {
  BIOME_REGISTRY,
  getRespawnRate,
  RESPAWN_BASE_INTERVAL,
} from '../data/biomes.config';
import { ResolvedFloor } from '../types/floor.types';
import { rollMonsterLevel } from '../utils/floor-resolver.utils';
import { ITile, IRoom, PlacedLocation } from '../types/map.types';
import { LOCATION_REGISTRY } from '../data/locations.config';
import { euclideanDistance } from '../utils/distance.utils';
import { isSpawnableCell } from '../utils/map.utils';
import { rollBoostItemId } from '../data/loot-pools.config';

/** Drop chance of a single `soul-shard` on a regular (non-boss) monster. */
const SOUL_SHARD_MOB_CHANCE = 0.05;

export class MonsterSpawningService {
  public dungeonService!: typeof dungeonService;
  private spawnerEnabled = false;
  private spawnerInterval = RESPAWN_BASE_INTERVAL;
  private spawnerTickCounter = 0;
  private ticksOnFloor = 0;
  private currentFloorConfig: ResolvedFloor | null = null;

  public setFloorConfig(config: ResolvedFloor): void {
    this.currentFloorConfig = config;
    this.ticksOnFloor = 0;
    this.spawnerTickCounter = 0;
  }

  public getCurrentFloorLevel(): number {
    return this.currentFloorConfig?.averageMonsterLevel ?? 1;
  }

  /** Rolls a monster level around the current floor's dungeon average. */
  public rollMonsterLevel(): number {
    return rollMonsterLevel(this.currentFloorConfig?.averageMonsterLevel ?? 1);
  }

  public getCurrentBiome(): string | null {
    return this.currentFloorConfig?.biome.id ?? null;
  }

  public setSpawnerEnabled(enabled: boolean): void {
    this.spawnerEnabled = enabled;
    if (enabled) {
      this.spawnerTickCounter = 0;
    }
  }

  public isSpawnerEnabled(): boolean {
    return this.spawnerEnabled;
  }

  public setSpawnerInterval(ticks: number): void {
    this.spawnerInterval = ticks;
  }

  public getSpawnerInterval(): number {
    return this.spawnerInterval;
  }

  public updateSpawner(deltaTime: number): void {
    if (!this.spawnerEnabled) {
      return;
    }
    this.ticksOnFloor += deltaTime;
    this.spawnerTickCounter += deltaTime;
    if (this.spawnerTickCounter >= this.spawnerInterval) {
      this.spawnerTickCounter = 0;
      const cfg = this.currentFloorConfig;
      if (!cfg) {
        return;
      }
      const rate = getRespawnRate(this.ticksOnFloor, cfg.biome.respawnRate);
      if (Math.random() < rate) {
        this.spawnRandomMonster();
      }
    }
  }

  private spawnRandomMonster(): void {
    const cfg = this.currentFloorConfig;
    if (!cfg) {
      return;
    }
    const biomeKey = cfg.biome.id;

    const selectedId = this.pickRandomMonsterIdForBiome(biomeKey);
    if (!selectedId) {
      return;
    }

    const pos = this.findValidSpawnPosition();
    if (!pos) {
      return;
    }
    const monster = this.createMonster(
      selectedId,
      pos.x,
      pos.y,
      this.rollMonsterLevel(),
    );
    if (monster) {
      this.dungeonService.addEntity(monster);
    }
  }

  public findValidSpawnPosition(): { x: number; y: number } | null {
    const players = this.dungeonService.getPlayers();
    if (players.length === 0) {
      return null;
    }
    const valid = this.dungeonService.mapService
      .getMapTiles()
      .filter(
        (tile) =>
          isSpawnableCell(tile.x, tile.y) &&
          players.every(
            (player) =>
              euclideanDistance(
                tile.x,
                tile.y,
                player.positionX,
                player.positionY,
              ) >= Math.max(4, player.stats.viewRange),
          ),
      );
    if (valid.length === 0) {
      return null;
    }
    return valid[Math.floor(Math.random() * valid.length)];
  }

  public findFreeCellNear(
    x: number,
    y: number,
  ): { x: number; y: number } | null {
    if (isSpawnableCell(x, y)) {
      return { x, y };
    }
    for (let r = 1; r <= 8; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dy = -r; dy <= r; dy++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
            continue;
          }
          const cx = x + dx;
          const cy = y + dy;
          if (isSpawnableCell(cx, cy)) {
            return { x: cx, y: cy };
          }
        }
      }
    }
    return this.findValidSpawnPosition();
  }

  public findSpawnPositionInRoom(room: IRoom): { x: number; y: number } | null {
    const cells = this.dungeonService.mapService
      .getRoomCells(room)
      .filter((c) => isSpawnableCell(c.x, c.y));
    if (cells.length === 0) {
      return null;
    }
    return cells[Math.floor(Math.random() * cells.length)];
  }

  public createMonster(
    configId: string,
    x: number,
    y: number,
    level: number,
  ): MonsterEntity | null {
    const baseConfig = MONSTER_CONFIGS[configId];
    if (!baseConfig) {
      return null;
    }

    const curve = baseConfig.scalingCurve;
    const scale = (base: number, rate: number) =>
      scaleMonsterStat(base, level, rate, curve);

    const scaledLife = scale(baseConfig.life, DEFAULT_STAT_RATES.life);
    // `maxLife` is the source of truth for the entity's life cap and is now
    // re-synced from `baseStats` on every `recomputeStats` — so the scaled life
    // must live in `baseStats.maxLife`, not only in the top-level `maxLife`
    // prop (otherwise recompute would reset it to the default).
    const scaledStats = { ...baseConfig.baseStats, maxLife: scaledLife };

    if (scaledStats.physicalAttack !== undefined) {
      scaledStats.physicalAttack = scale(
        scaledStats.physicalAttack,
        DEFAULT_STAT_RATES.physicalAttack,
      );
    }
    if (scaledStats.magicAttack !== undefined) {
      scaledStats.magicAttack = scale(
        scaledStats.magicAttack,
        DEFAULT_STAT_RATES.magicAttack,
      );
    }
    if (scaledStats.physicalDefense !== undefined) {
      scaledStats.physicalDefense = scale(
        scaledStats.physicalDefense,
        DEFAULT_STAT_RATES.physicalDefense,
      );
    }
    if (scaledStats.magicDefense !== undefined) {
      scaledStats.magicDefense = scale(
        scaledStats.magicDefense,
        DEFAULT_STAT_RATES.magicDefense,
      );
    }

    const monster = new MonsterEntity({
      maxLife: scaledLife,
      isWalkableOver: baseConfig.isWalkableOver,
      knockbackable: baseConfig.knockbackable ?? true,
      configId: baseConfig.configId,
      x,
      y,
      stats: scaledStats,
      level,
    });
    monster.initializeBuffs();
    monster.mapIcon = baseConfig.mapIcon ?? 'normal';

    const eliteChance = this.currentFloorConfig?.biome.eliteChance ?? 0;
    if (
      baseConfig.eligibleForElite &&
      eliteChance > 0 &&
      Math.random() < eliteChance
    ) {
      monster.makeElite();
    }

    return monster;
  }

  public populateFloor(
    tiles: ITile[],
    floorConfig: ResolvedFloor,
    rooms: IRoom[],
    placedLocations: PlacedLocation[] = [],
  ): void {
    const players = this.dungeonService.getPlayers();
    if (players.length === 0) {
      return;
    }

    if (floorConfig.floorType === 'safe') {
      return;
    }

    // Boss floors skip the regular mob / mini-boss fill and only spawn the boss.
    if (floorConfig.floorType === 'boss') {
      this.spawnFloorBoss(floorConfig, rooms);
      return;
    }

    const groundTiles = tiles.filter((t) => t.type === 'ground');
    // Initial floor density: 1 monster per ~70 ground tiles → ~10 mobs on a
    // 40×30 floor, consistent with the "10-15 mobs / floor" design. The respawn
    // spawner then ramps via updateSpawner.
    const monsterCount = Math.max(1, Math.floor(groundTiles.length / 70));

    const biomeKey = floorConfig.biome.id;

    for (let i = 0; i < monsterCount; i++) {
      const valid = groundTiles.filter(
        (t) =>
          isSpawnableCell(t.x, t.y) &&
          players.every(
            (player) =>
              euclideanDistance(t.x, t.y, player.positionX, player.positionY) >=
              4,
          ),
      );
      const pos =
        valid.length > 0
          ? valid[Math.floor(Math.random() * valid.length)]
          : null;
      if (!pos) {
        continue;
      }

      const selectedId = this.pickRandomMonsterIdForBiome(biomeKey);
      if (!selectedId) {
        continue;
      }

      const monster = this.createMonster(
        selectedId,
        pos.x,
        pos.y,
        this.rollMonsterLevel(),
      );
      if (monster) {
        this.dungeonService.addEntity(monster);
      }
    }

    for (const placed of placedLocations) {
      const def = LOCATION_REGISTRY[placed.id];
      if (!def?.miniBossId) {
        continue;
      }
      const miniBoss = this.createMonster(
        def.miniBossId,
        placed.x,
        placed.y,
        floorConfig.averageMonsterLevel,
      );
      if (miniBoss) {
        miniBoss.mapIcon = 'miniboss';
        miniBoss.aiEnabled = false;
        miniBoss.dormantRoomIndex = placed.roomIndex;
        this.dungeonService.addEntity(miniBoss);
      }
    }
  }

  private spawnFloorBoss(floorConfig: ResolvedFloor, rooms: IRoom[]): void {
    const bossMonsterId = floorConfig.bossMonsterId;
    if (!bossMonsterId || floorConfig.bossLevel === undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        `Floor ${floorConfig.floor} is a boss floor but bossMonsterId/bossLevel is not set; skipping boss spawn.`,
      );
      return;
    }
    const bossRoom = rooms[0];
    const bossPos =
      this.findSpawnPositionInRoom(bossRoom) ??
      (bossRoom.cells.length > 0
        ? bossRoom.cells[Math.floor(bossRoom.cells.length / 2)]
        : {
            x: Math.floor(bossRoom.x + bossRoom.width / 2),
            y: Math.floor(bossRoom.y + bossRoom.height / 2),
          });
    const boss = this.createMonster(
      bossMonsterId,
      bossPos.x,
      bossPos.y,
      floorConfig.bossLevel,
    );
    if (boss) {
      boss.isFloorBoss = true;
      boss.mapIcon = 'boss';
      this.dungeonService.addEntity(boss);
    }
  }

  /**
   * Fixed dummy layout for the `test-arena` dungeon: AI-disabled monsters with
   * defenses zeroed (clean damage readout) and HP among 10 / 100 / 1000. Tight
   * 3×3 packs (AOE testing) plus a north column and spread-out singles
   * (single-target / line / cone). Offsets are relative to the room center
   * (the player spawn). cf. docs/world.md "Arène de test".
   */
  public populateTestArena(room: IRoom): void {
    const cx = Math.floor(room.x + room.width / 2);
    const cy = Math.floor(room.y + room.height / 2);

    const spawn = (dx: number, dy: number, hp: number): void => {
      const m = this.createMonster('frog', cx + dx, cy + dy, 1);
      if (!m) {
        return;
      }
      m.baseStats = {
        ...m.baseStats,
        maxLife: hp,
        physicalDefense: 0,
        magicDefense: 0,
      };
      m.maxLife = hp;
      m.life = hp;
      m.aiEnabled = false;
      m.aiLocked = true;
      this.dungeonService.addEntity(m);
    };

    // Tight 3×3 packs for AOE: left pack 100 HP, right pack 1000 HP.
    for (let dx = -7; dx <= -5; dx++) {
      for (let dy = -8; dy <= -6; dy++) {
        spawn(dx, dy, 100);
      }
    }
    for (let dx = 5; dx <= 7; dx++) {
      for (let dy = -8; dy <= -6; dy++) {
        spawn(dx, dy, 1000);
      }
    }

    // North column (aim line / cone / projectiles upward).
    spawn(0, -4, 1000);
    spawn(0, -6, 100);
    spawn(0, -8, 10);

    // Spread-out singles, mixed HP.
    spawn(-9, 0, 10);
    spawn(-5, 0, 100);
    spawn(5, 0, 10);
    spawn(9, 0, 1000);
    spawn(-6, 8, 100);
    spawn(0, 8, 1000);
    spawn(6, 8, 10);
  }

  public pickRandomMonsterIdForBiome(biomeId: string): string | null {
    const weights = BIOME_REGISTRY[biomeId]?.spawnWeights ?? {};
    const entries = Object.entries(weights).filter(([, w]) => w > 0);
    if (entries.length === 0) {
      return null;
    }
    const totalW = entries.reduce((s, [, w]) => s + w, 0);
    let roll = Math.random() * totalW;
    for (const [cid, w] of entries) {
      roll -= w;
      if (roll <= 0) {
        return cid;
      }
    }
    return entries[0]?.[0] ?? null;
  }

  /**
   * Standard roll + the monster's `eliteLoot` (if elite). Items go on
   * (x,y). `multiplier` (default 1) is injected by `MonsterEntity.death`
   * when the killer carries a `mod-rich-drop-tracker` buff linked to the
   * spell that killed. `isBoss` flags a floor boss for the universal
   * affection-material rule (cf. `rollAffectionDrop`).
   */
  public rollMonsterLoot(
    configId: string,
    x: number,
    y: number,
    isElite = false,
    multiplier = 1,
    isBoss = false,
    firstKill = false,
  ): void {
    const baseConfig = MONSTER_CONFIGS[configId];
    if (!baseConfig) {
      return;
    }
    const finalMultiplier = multiplier * getTalentLootMultiplier();
    rollLootTable(baseConfig.loot, [{ x, y }], finalMultiplier, firstKill);
    if (isElite && baseConfig.eliteLoot) {
      rollLootTable(baseConfig.eliteLoot, [{ x, y }], finalMultiplier);
    }
    this.rollAffectionDrop([{ x, y }], isBoss, baseConfig.isMiniBoss ?? false);
  }

  /**
   * Variant of `rollMonsterLoot` that spreads item drops over a list of
   * positions (cyclic rotation). Useful for bosses whose drops would
   * otherwise stack under the staircase dropped on their death.
   */
  public rollMonsterLootSpread(
    configId: string,
    positions: { x: number; y: number }[],
    isElite = false,
    multiplier = 1,
    isBoss = false,
    firstKill = false,
  ): void {
    const baseConfig = MONSTER_CONFIGS[configId];
    if (!baseConfig || positions.length === 0) {
      return;
    }
    const finalMultiplier = multiplier * getTalentLootMultiplier();
    rollLootTable(baseConfig.loot, positions, finalMultiplier, firstKill);
    if (isElite && baseConfig.eliteLoot) {
      rollLootTable(baseConfig.eliteLoot, positions, finalMultiplier);
    }
    this.rollAffectionDrop(positions, isBoss, baseConfig.isMiniBoss ?? false);
  }

  /**
   * Universal affection-material drop, applied to every monster on top of its
   * own loot table (cf. docs/monsters.md). Not affected by loot multipliers —
   * the amounts are fixed by monster class:
   * - floor boss → 1 `spirit-heart` (100%),
   * - mini-boss → 3 `soul-shard` (fixed),
   * - any other monster → 5% chance of 1 `soul-shard`.
   * Bosses and mini-bosses also drop one boost item (boss-signature filter).
   */
  private rollAffectionDrop(
    positions: { x: number; y: number }[],
    isBoss: boolean,
    isMiniBoss: boolean,
  ): void {
    if (positions.length === 0) {
      return;
    }
    const { x, y } = positions[0];
    const inv = dungeonService.inventoryService;
    if (isBoss) {
      inv.dropItem(x, y, 'spirit-heart', 1);
    } else if (isMiniBoss) {
      inv.dropItem(x, y, 'soul-shard', 3);
    } else if (Math.random() < SOUL_SHARD_MOB_CHANCE) {
      inv.dropItem(x, y, 'soul-shard', 1);
    }
    if (isBoss || isMiniBoss) {
      this.rollBoostDrop(x, y, 'boss-signature');
    }
  }

  private rollBoostDrop(x: number, y: number, source: string): void {
    const itemId = rollBoostItemId(source);
    if (!itemId) {
      return;
    }
    dungeonService.inventoryService.dropItem(x, y, itemId, 1);
  }
}
